import {
  AboutYouPage,
  AboutYouQuestion,
} from "@appally/collections/AboutYouQuestion";
import {
  AboutYourWorkPage,
  AboutYourWorkQuestion,
} from "@appally/collections/AboutYourWorkQuestion";
import { AreaOfChallengeWithQuestions } from "@appally/collections/AreaOfChallenge";
import { Task } from "@appally/collections/Task";
import { WorkplaceChallengeQuestion } from "@appally/collections/WorkplaceChallengeQuestion";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { groupBy } from "lodash";
import {
  getAboutYouQuestions,
  getAboutYourWorkQuestions,
  getAreasOfChallengeWithQuestions,
  getTasks,
  getWorkplaceChallengesQuestions,
} from "../actions/questions";

// Define a service using a base URL and expected endpoints
export const questionsApi = createApi({
  reducerPath: "questions",
  baseQuery: fakeBaseQuery(),
  tagTypes: [
    "AboutYou",
    "AboutYouPages",
    "AboutYourJob",
    "AboutYourJobPages",
    "WorkplaceChallengePages",
    "WorkplaceChallengeQuestions",
    "AllQuestions",
  ],
  endpoints: (builder) => ({
    // Pages with questions for the about you section
    aboutYouPages: builder.query<AboutYouPage[], void>({
      queryFn: async () => {
        const questions = await getAboutYouQuestions();

        // Group questions by page number and extract the page title from each first question
        const groupedQuestions = groupBy(questions, "page_number");
        const data = Object.entries(groupedQuestions).map(
          ([page_number, questions]) => ({
            id: page_number,
            page_number: parseInt(page_number),
            page_title: questions[0].page_title,
            questions,
          })
        );

        return { data };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "AboutYouPages" as const,
                id,
              })),
              { type: "AboutYouPages", id: "LIST" },
            ]
          : [{ type: "AboutYouPages", id: "LIST" }],
    }),

    // Questions for the about you section
    aboutYouQuestions: builder.query<AboutYouQuestion[], void>({
      queryFn: async () => {
        const data = await getAboutYouQuestions();
        return { data };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "AboutYou" as const, id })),
              { type: "AboutYou", id: "LIST" },
            ]
          : [{ type: "AboutYou", id: "LIST" }],
    }),
    // Pages with questions for the about you section
    aboutYourJobPages: builder.query<AboutYourWorkPage[], void>({
      queryFn: async () => {
        const questions = await getAboutYourWorkQuestions();

        // Group questions by page number and extract the page title from each first question
        const groupedQuestions = groupBy(questions, "page_number");
        const data = Object.entries(groupedQuestions).map(
          ([page_number, questions]) => ({
            id: page_number,
            page_number: parseInt(page_number),
            page_title: questions[0].page_title,
            questions,
          })
        );

        return { data };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "AboutYourJobPages" as const,
                id,
              })),
              { type: "AboutYourJobPages", id: "LIST" },
            ]
          : [{ type: "AboutYourJobPages", id: "LIST" }],
    }),

    // Questions for the about your job section
    aboutYourJobQuestions: builder.query<AboutYourWorkQuestion[], void>({
      queryFn: async () => {
        const data = await getAboutYourWorkQuestions();
        return { data };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "AboutYourJob" as const,
                id,
              })),
              { type: "AboutYourJob", id: "LIST" },
            ]
          : [{ type: "AboutYourJob", id: "LIST" }],
    }),

    // Areas and questions for the workplace challenges section
    workplaceChallengePages: builder.query<
      AreaOfChallengeWithQuestions[],
      void
    >({
      queryFn: async () => {
        const data = await getAreasOfChallengeWithQuestions();
        return { data };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ page_number }) => ({
                type: "WorkplaceChallengePages" as const,
                page_number,
              })),
              { type: "WorkplaceChallengePages", id: "LIST" },
            ]
          : [{ type: "WorkplaceChallengePages", id: "LIST" }],
    }),

    // Questions for the workplace challenges section
    workplaceChallengeQuestions: builder.query<
      WorkplaceChallengeQuestion[],
      void
    >({
      queryFn: async () => {
        const data = await getWorkplaceChallengesQuestions();
        return { data };
      },
    }),

    // All questions in one array
    allQuestions: builder.query<
      (AboutYouQuestion | AboutYourWorkQuestion | WorkplaceChallengeQuestion)[],
      void
    >({
      queryFn: async () => {
        const aboutYou = await getAboutYouQuestions();
        const aboutYourJob = await getAboutYourWorkQuestions();
        const workplaceChallenges = await getWorkplaceChallengesQuestions();
        const data = [...aboutYou, ...aboutYourJob, ...workplaceChallenges];
        return { data };
      },
      providesTags: ["AllQuestions"],
    }),

    // All questions in one array
    getTasks: builder.query<Task[], void>({
      queryFn: async () => {
        const data = await getTasks();
        return { data };
      },
      providesTags: ["AllQuestions"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useAboutYouQuestionsQuery,
  useAboutYouPagesQuery,
  useAboutYourJobQuestionsQuery,
  useAboutYourJobPagesQuery,
  useWorkplaceChallengePagesQuery,
  useWorkplaceChallengeQuestionsQuery,
  useAllQuestionsQuery,
  useGetTasksQuery,
} = questionsApi;
